// Browsing the Market: categories, currencies, filters, sorting and paging.
// The server keeps one entry per open listing (server/market-index.ts) and
// answers a MarketQuery with one page of rows. Stackable items show as one
// row per item kind (lowest price, units on offer, how many listings), like
// a commodity board; creatures, held items, relics and shrines are one of a
// kind and show one row per listing.
//
// Currency: every listing is priced in one currency, the seller's choice:
// coins, or gems (shared/gems.ts) while the gem switch is on. The server says
// which are open; a query for a closed one drops it. With no currency picked,
// rows sort by currency first, so prices are only ever compared like for like.
import { ITEM } from './items.ts';

export type MarketCurrency = 'coins' | 'gems';
export const MARKET_CURRENCIES: { id: MarketCurrency; name: string }[] = [
  { id: 'coins', name: 'Coins' },
  { id: 'gems', name: 'Gems' },
];
const curOrder = (c: MarketCurrency) => MARKET_CURRENCIES.findIndex((x) => x.id === c);

export type MarketCat = 'all' | 'creature' | 'held' | 'relic' | 'shrine' | 'stones' | 'materials' | 'charms' | 'keys';
/** Sidebar order. Unique things first, then stackable goods. */
export const MARKET_CATS: { id: MarketCat; name: string }[] = [
  { id: 'all', name: 'Everything' },
  { id: 'creature', name: 'Creatures' },
  { id: 'held', name: 'Held items' },
  { id: 'relic', name: 'Relics' },
  { id: 'shrine', name: 'Shrines' },
  { id: 'stones', name: 'Stones' },
  { id: 'materials', name: 'Materials' },
  { id: 'charms', name: 'Charms' },
  { id: 'keys', name: 'Boss keys' },
];

/** The category a listing files under: unique kinds by kind, bag items by item kind. */
export function catOf(kind: string, ref: string): Exclude<MarketCat, 'all'> {
  if (kind === 'creature' || kind === 'held' || kind === 'relic' || kind === 'shrine') return kind;
  const k = ITEM[ref]?.kind;
  if (k === 'ascension' || k === 'awakening') return 'stones';
  if (k === 'boost') return 'charms';
  if (k === 'token') return 'keys';
  return 'materials';
}

export type MarketSort = 'price' | '-price' | 'new' | 'grade' | 'level' | 'quality' | 'qty';
export const MARKET_SORTS: MarketSort[] = ['price', '-price', 'new', 'grade', 'level', 'quality', 'qty'];

export interface MarketQuery {
  cat?: MarketCat;
  /** Name, species or seller text. */
  q?: string;
  sort?: MarketSort;
  cur?: MarketCurrency;
  /** 0-based page. */
  page?: number;
  /** Rows per page: as many as fit the viewer's screen (MARKET_PER_MIN to MARKET_PAGE); MARKET_PAGE when unset. */
  per?: number;
  /** Creatures: an element, a region, the lowest grade score, Aura only, the lowest Bloodline roll (0-4), a level range. */
  el?: string;
  region?: string;
  minScore?: number;
  aura?: boolean;
  blood?: number;
  lvMin?: number;
  lvMax?: number;
  /** Held items, relics: the lowest quality. */
  minQ?: number;
  /** Price each, in the query's currency. */
  pMin?: number;
  pMax?: number;
  /** Who sells: everyone, players only, or the Exchange only. */
  seller?: '' | 'players' | 'house';
  /** One item kind's order book (stackable items). */
  item?: string;
  /** A listing to send along whatever the page (a Trade card's View listing). */
  focus?: number;
}

export const MARKET_PAGE = 30;
export const MARKET_PER_MIN = 3;

/** What the server knows about one open listing, for filtering and sorting. */
export interface MarketEntry {
  id: number;
  kind: string;
  ref: string;
  cat: Exclude<MarketCat, 'all'>;
  cur: MarketCurrency;
  /** Price each (an old lot's price spread over its units). */
  unit: number;
  /** An old lot from before unit prices: sells whole, so a bulk buy skips it. */
  lot?: boolean;
  qty: number;
  created: number;
  openAt: number;
  seller: number;
  house: boolean;
  /** Lowercase search text: the thing's name, species and seller. */
  text: string;
  species?: string;
  elements?: string[];
  region?: string;
  score?: number;
  level?: number;
  aura?: boolean;
  blood?: number;
  quality?: number;
}

/** A row on the board: one listing, or one item kind with all its listings folded together. */
export type MarketRow =
  | { t: 'one'; id: number }
  | { t: 'group'; ref: string; cur: MarketCurrency; low: number; qty: number; n: number; newest: number; avg?: number | null };

const stackable = (e: MarketEntry) => e.kind === 'item';
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** Clean a query from the wire: unknown fields drop, numbers clamp. */
export function cleanQuery(raw: unknown, curs: MarketCurrency[] = ['coins']): MarketQuery {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const q: MarketQuery = {};
  if (MARKET_CATS.some((c) => c.id === r.cat)) q.cat = r.cat as MarketCat;
  if (typeof r.q === 'string' && r.q.trim()) q.q = r.q.trim().toLowerCase().slice(0, 40);
  if (MARKET_SORTS.includes(r.sort as MarketSort)) q.sort = r.sort as MarketSort;
  if (curs.length > 1 && curs.includes(r.cur as MarketCurrency)) q.cur = r.cur as MarketCurrency;
  const page = num(r.page); if (page !== undefined) q.page = Math.max(0, Math.min(10_000, Math.floor(page)));
  const per = num(r.per); if (per !== undefined) q.per = Math.max(MARKET_PER_MIN, Math.min(MARKET_PAGE, Math.floor(per)));
  if (typeof r.el === 'string' && r.el.length <= 16) q.el = r.el;
  if (typeof r.region === 'string' && r.region.length <= 16) q.region = r.region;
  for (const k of ['minScore', 'blood', 'lvMin', 'lvMax', 'minQ', 'pMin', 'pMax'] as const) { const v = num(r[k]); if (v !== undefined && v > 0) q[k] = v; }
  if (r.aura === true) q.aura = true;
  if (r.seller === 'players' || r.seller === 'house') q.seller = r.seller;
  if (typeof r.item === 'string' && r.item.length <= 64) q.item = r.item;
  const focus = num(r.focus); if (focus !== undefined && focus > 0) q.focus = Math.floor(focus);
  return q;
}

/** Does this entry pass the query's filters (all but the category)? */
function passes(e: MarketEntry, q: MarketQuery, viewer: number): boolean {
  if (e.seller === viewer) return false;
  if (q.cur && e.cur !== q.cur) return false;
  if (q.seller === 'players' && e.house) return false;
  if (q.seller === 'house' && !e.house) return false;
  if (q.q && !e.text.includes(q.q)) return false;
  if (q.pMin && e.unit < q.pMin) return false;
  if (q.pMax && e.unit > q.pMax) return false;
  if (e.kind === 'creature') {
    if (q.el && !e.elements?.includes(q.el)) return false;
    if (q.region && e.region !== q.region) return false;
    if (q.minScore && (e.score ?? 0) < q.minScore) return false;
    if (q.aura && !e.aura) return false;
    if (q.blood && (e.blood ?? 0) < q.blood) return false;
    if (q.lvMin && (e.level ?? 0) < q.lvMin) return false;
    if (q.lvMax && (e.level ?? 0) > q.lvMax) return false;
  }
  if ((e.kind === 'held' || e.kind === 'relic') && q.minQ && (e.quality ?? 0) < q.minQ) return false;
  return true;
}

export interface MarketPage {
  rows: MarketRow[];
  total: number;
  page: number;
  pages: number;
  /** Rows per category under the current filters (other players' listings). */
  counts: Partial<Record<MarketCat, number>>;
  /** Listing ids the page shows (the server sends their full listings). */
  ids: number[];
}

/**
 * One page of the board. Another player's open listings only (your own are under
 * Your listings). Groups sort by their lowest price and newest listing.
 */
export function queryMarket(all: MarketEntry[], q: MarketQuery, viewer: number): MarketPage {
  const cat = q.cat ?? 'all';
  const pass = all.filter((e) => passes(e, q, viewer));
  // Rows per category, so the sidebar counts match what the filters leave.
  const counts: Partial<Record<MarketCat, number>> = {};
  const seen = new Set<string>();
  for (const e of pass) {
    if (stackable(e)) { const k = `${e.cur}:${e.ref}`; if (seen.has(k)) continue; seen.add(k); }
    counts[e.cat] = (counts[e.cat] ?? 0) + 1;
    counts.all = (counts.all ?? 0) + 1;
  }
  const inCat = cat === 'all' ? pass : pass.filter((e) => e.cat === cat);
  // Fold stackable items into one row per item kind and currency.
  type Sortable = { row: MarketRow; cur: number; unit: number; newest: number; score: number; level: number; quality: number; qty: number };
  const groups = new Map<string, Sortable & { row: Extract<MarketRow, { t: 'group' }> }>();
  const rows: Sortable[] = [];
  for (const e of inCat) {
    if (!stackable(e)) {
      rows.push({ row: { t: 'one', id: e.id }, cur: curOrder(e.cur), unit: e.unit, newest: e.created, score: e.score ?? -1, level: e.level ?? -1, quality: e.quality ?? -1, qty: e.qty });
      continue;
    }
    const k = `${e.cur}:${e.ref}`;
    const g = groups.get(k);
    if (!g) {
      const s = { row: { t: 'group' as const, ref: e.ref, cur: e.cur, low: e.unit, qty: e.qty, n: 1, newest: e.created }, cur: curOrder(e.cur), unit: e.unit, newest: e.created, score: -1, level: -1, quality: -1, qty: e.qty };
      groups.set(k, s); rows.push(s);
    } else {
      g.row.low = Math.min(g.row.low, e.unit); g.unit = g.row.low;
      g.row.qty += e.qty; g.qty = g.row.qty;
      g.row.n++;
      g.row.newest = Math.max(g.row.newest, e.created); g.newest = g.row.newest;
    }
  }
  const by: Record<MarketSort, (a: Sortable, b: Sortable) => number> = {
    price: (a, b) => a.unit - b.unit || b.newest - a.newest,
    '-price': (a, b) => b.unit - a.unit || b.newest - a.newest,
    new: (a, b) => b.newest - a.newest,
    grade: (a, b) => b.score - a.score || a.unit - b.unit,
    level: (a, b) => b.level - a.level || a.unit - b.unit,
    quality: (a, b) => b.quality - a.quality || a.unit - b.unit,
    qty: (a, b) => b.qty - a.qty || a.unit - b.unit,
  };
  // Prices in different currencies don't compare: coins first, then gems, each in the asked order.
  const sort = q.sort ?? 'price', cmp = by[sort];
  rows.sort(sort === 'new' ? cmp : (a, b) => a.cur - b.cur || cmp(a, b));
  const per = q.per ?? MARKET_PAGE;
  const pages = Math.max(1, Math.ceil(rows.length / per));
  const page = Math.min(q.page ?? 0, pages - 1);
  const slice = rows.slice(page * per, (page + 1) * per).map((s) => s.row);
  const ids = slice.flatMap((r) => (r.t === 'one' ? [r.id] : []));
  return { rows: slice, total: rows.length, page, pages, counts, ids };
}

/** One item kind's sell orders, cheapest first (ties: oldest first), other players' only. */
export function orderBook(all: MarketEntry[], ref: string, cur: MarketCurrency, viewer: number): MarketEntry[] {
  return all.filter((e) => e.kind === 'item' && e.ref === ref && e.cur === cur && e.seller !== viewer)
    .sort((a, b) => a.unit - b.unit || a.created - b.created);
}

/**
 * Plan a buy of `qty` units at the best prices: walks the open (past their hold)
 * listings cheapest first. Returns the units per listing and the total, or
 * fewer units when the book runs out.
 */
export function planBuy(book: MarketEntry[], qty: number, now: number): { take: { id: number; qty: number; unit: number }[]; units: number; total: number } {
  const take: { id: number; qty: number; unit: number }[] = [];
  let units = 0, total = 0;
  for (const e of book) {
    if (units >= qty) break;
    if (e.openAt > now || e.lot) continue;
    const n = Math.min(e.qty, qty - units);
    take.push({ id: e.id, qty: n, unit: e.unit });
    units += n; total += n * e.unit;
  }
  return { take, units, total };
}
